import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAdminAuth } from '@/context/AdminAuthContext';

export default function AdminLoginModal() {
  const { isOpen, closeModal, login, auth } = useAdminAuth();
  const navigate = useNavigate();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const usernameRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isOpen) {
      setUsername('');
      setPassword('');
      setError('');
      setLoading(false);
      setTimeout(() => usernameRef.current?.focus(), 150);
    }
  }, [isOpen]);

  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) closeModal();
    };
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [isOpen, closeModal]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    setTimeout(() => {
      const success = login(username, password);
      if (success) {
        navigate('/admin');
      } else {
        setError('Usuario o contraseña incorrectos');
        setLoading(false);
      }
    }, 600);
  };

  if (!isOpen) return null;

  if (auth.isLoggedIn) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center">
      <div
        className="absolute inset-0 bg-black/70 backdrop-blur-sm"
        onClick={closeModal}
      ></div>

      <div className="relative z-10 bg-background-100 border border-white/10 rounded-2xl w-full max-w-md mx-4 p-8 animate-in fade-in zoom-in">
        <button
          onClick={closeModal}
          className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center rounded-full bg-white/5 hover:bg-white/10 text-foreground-400 hover:text-foreground-50 transition-all cursor-pointer"
        >
          <i className="ri-close-line w-4 h-4 flex items-center justify-center"></i>
        </button>

        <div className="text-center mb-6">
          <div className="w-14 h-14 flex items-center justify-center bg-primary-500/15 rounded-full mx-auto mb-4">
            <i className="ri-shield-user-line text-primary-500 text-2xl w-7 h-7 flex items-center justify-center"></i>
          </div>
          <h2 className="text-foreground-50 text-xl font-bold">Acceso Administrativo</h2>
          <p className="text-foreground-400 text-sm mt-1">Ingresa tus credenciales para acceder al panel</p>
        </div>

        <form onSubmit={handleSubmit} className="flex flex-col gap-4">
          <div>
            <label className="block text-foreground-50 text-sm font-semibold mb-1.5">Usuario</label>
            <div className="relative">
              <div className="absolute left-4 top-1/2 -translate-y-1/2 text-foreground-400">
                <i className="ri-user-line w-4 h-4 flex items-center justify-center"></i>
              </div>
              <input
                ref={usernameRef}
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="Ingresa tu usuario"
                className="w-full bg-white/5 border border-white/10 text-foreground-50 placeholder-foreground-400 text-sm rounded-lg pl-11 pr-4 py-3 outline-none focus:border-primary-500 focus:bg-white/10 transition-all"
                required
                autoComplete="username"
              />
            </div>
          </div>

          <div>
            <label className="block text-foreground-50 text-sm font-semibold mb-1.5">Contraseña</label>
            <div className="relative">
              <div className="absolute left-4 top-1/2 -translate-y-1/2 text-foreground-400">
                <i className="ri-lock-line w-4 h-4 flex items-center justify-center"></i>
              </div>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Ingresa tu contraseña"
                className="w-full bg-white/5 border border-white/10 text-foreground-50 placeholder-foreground-400 text-sm rounded-lg pl-11 pr-4 py-3 outline-none focus:border-primary-500 focus:bg-white/10 transition-all"
                required
                autoComplete="current-password"
              />
            </div>
          </div>

          {error && (
            <div className="flex items-center gap-2 bg-red-500/10 border border-red-500/30 rounded-lg px-4 py-2.5">
              <i className="ri-error-warning-line text-red-400 w-4 h-4 flex items-center justify-center shrink-0"></i>
              <p className="text-red-400 text-xs">{error}</p>
            </div>
          )}

          <button
            type="submit"
            disabled={loading}
            className="whitespace-nowrap w-full bg-primary-500 hover:bg-primary-600 disabled:opacity-60 text-white font-bold py-3.5 rounded-lg text-sm flex items-center justify-center gap-2 transition-all duration-200 cursor-pointer mt-1"
          >
            {loading ? (
              <><i className="ri-loader-4-line animate-spin w-5 h-5 flex items-center justify-center"></i> Verificando...</>
            ) : (
              <><i className="ri-login-box-line w-5 h-5 flex items-center justify-center"></i> Ingresar</>
            )}
          </button>
        </form>
      </div>
    </div>
  );
}